import { type FC, type ReactNode } from "react";

interface ProcessingOption {
  speed: string;
  unit: string;
  label: string;
  tag: string;
  tagColor: string;
  features: string[];
  highlight: boolean;
  emergency?: boolean;
  smartSaver?: boolean;
  muted?: boolean;
}

interface SharedProcessingTimeSectionProps {
  sectionId?: string;
  cityName: string;
  headingExtra?: string;
  introText?: string;
  options: ProcessingOption[];
  emergencyDescription: string;
  disclaimerText: string;
  afterCardsContent?: ReactNode;
}

const SharedProcessingTimeSection: FC<SharedProcessingTimeSectionProps> = ({
  sectionId = "processing-options",
  cityName,
  headingExtra = "",
  introText,
  options,
  emergencyDescription,
  disclaimerText,
  afterCardsContent,
}) => {
  return (
    <section id={sectionId} className="bg-white pt-8 pb-20 md:pt-10 md:pb-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-10">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Processing Times</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
            {headingExtra
              ? `Expedited Passport Processing Options in ${cityName}, ${headingExtra}`
              : `Passport Processing Options in ${cityName}`}
          </h2>
          {introText && (
            <p className="text-gray-500 max-w-4xl mx-auto text-base leading-relaxed">
              {introText}
            </p>
          )}
        </div>

        {/* Educational Banner — 3 Ways to Get a Passport */}
        <div className="bg-[#F6C344] rounded-xl p-3 md:p-3 mb-10">
          <p className="text-[#1A365D] text-xs font-bold uppercase tracking-[0.2em] text-center mb-2">
            3 Ways to Get a U.S. Passport
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            {/* Path 1: Courier — equal size */}
            <div className="bg-[#1A365D] rounded-lg p-2.5 text-left">
              <div className="flex items-center gap-1.5 mb-1">
                <div className="w-6 h-6 flex-shrink-0 flex items-center justify-center bg-[#F6C344]/20 rounded-full">
                  <i className="ri-shield-check-line text-[#F6C344] text-xs"></i>
                </div>
                <div>
                  <p className="text-[#F6C344] text-[9px] font-bold uppercase tracking-wider">Option 1</p>
                  <p className="text-white font-bold text-[11px]">Registered Courier</p>
                </div>
              </div>
              <div className="flex items-center gap-1.5 mb-1">
                <div>
                  <p className="text-[#F6C344] text-[9px] font-bold uppercase tracking-wider mb-0.5">Urgent</p>
                  <p className="text-[#F6C344] font-bold text-sm">2–9 Days</p>
                </div>
                <div className="w-px h-6 bg-[#F6C344]/30"></div>
                <div>
                  <p className="text-[#F6C344] text-[9px] font-bold uppercase tracking-wider mb-0.5">Standard</p>
                  <p className="text-[#F6C344] font-bold text-sm">3–5 Weeks</p>
                </div>
              </div>
              <p className="text-white/80 text-[11px] leading-relaxed">
                The U.S. Department of State created the registered courier program to give travelers another option. For urgent travel, registered couriers have daily submission availability that can deliver passports in as little as 2–9 days. For travelers with more time, a 3–5 week service provides personalized support throughout the entire process.
              </p>
            </div>

            {/* Path 2: Post Office — equal size */}
            <div className="bg-[#1A365D] rounded-lg p-2.5 text-left">
              <div className="flex items-center gap-1.5 mb-1">
                <div className="w-6 h-6 flex-shrink-0 flex items-center justify-center bg-[#F6C344]/20 rounded-full">
                  <i className="ri-mail-send-line text-[#F6C344] text-xs"></i>
                </div>
                <div>
                  <p className="text-[#F6C344] text-[9px] font-bold uppercase tracking-wider">Option 2</p>
                  <p className="text-white font-bold text-[11px]">Post Office / Mail-In</p>
                </div>
              </div>
              <p className="text-[#F6C344] font-bold text-sm mb-0.5">1–2 Months</p>
              <p className="text-white/80 text-[11px] leading-relaxed">
                The DIY route is the option most travelers know. Apply at a local acceptance facility or renew by mail or online when eligible. It's often the lowest-cost option and works well if you have plenty of time before your trip.

If an issue is discovered after submission, you'll be notified by USPS and your application will be placed on hold until the problem is resolved by mail. Not recommended within 30 days of departure.
              </p>
            </div>

            {/* Path 3: Regional Agency — equal size */}
            <div className="bg-[#1A365D] rounded-lg p-2.5 text-left">
              <div className="flex items-center gap-1.5 mb-1">
                <div className="w-6 h-6 flex-shrink-0 flex items-center justify-center bg-[#F6C344]/20 rounded-full">
                  <i className="ri-alarm-warning-line text-[#F6C344] text-xs"></i>
                </div>
                <div>
                  <p className="text-[#F6C344] text-[9px] font-bold uppercase tracking-wider">Option 3</p>
                  <p className="text-white font-bold text-[11px]">Regional Agency</p>
                </div>
              </div>
              <p className="text-[#F6C344] font-bold text-sm mb-0.5">24 Hours</p>
              <p className="text-white/80 text-[11px] leading-relaxed">
                Emergency-only in-person visits at a U.S. Department of State Passport Agency. Requires a confirmed NPIC appointment and documented urgent travel with proof. Applicants should expect to spend most of the day at the agency and may be instructed to return the following business day to pick up their passport.
              </p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {options.map((opt, i) => (
            <div
              key={i}
              className={`relative rounded-lg p-6 flex flex-col ${
                opt.highlight
                  ? "bg-[#1A365D] text-white border-2 border-[#F6C344]"
                  : opt.emergency
                  ? "bg-[#F7F8FA] border border-gray-200"
                  : opt.smartSaver
                  ? "bg-[#F7F8FA] border-2 border-green-400"
                  : "bg-[#F7F8FA] border border-gray-200"
              }`}
            >
              {opt.highlight && (
                <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 bg-[#F6C344] text-[#1A365D] text-xs font-bold px-4 py-1 rounded-full whitespace-nowrap">
                  Recommended
                </div>
              )}
              <span className={`self-start text-xs font-semibold px-2.5 py-1 rounded-full mb-4 whitespace-nowrap ${opt.tagColor}`}>
                {opt.tag}
              </span>
              <div className="mb-1">
                <span className={`font-serif text-4xl font-bold ${opt.highlight ? "text-[#F6C344]" : "text-[#1A365D]"}`}>
                  {opt.speed}
                </span>
              </div>
              <p className={`text-sm font-semibold mb-1 ${opt.highlight ? "text-white/70" : "text-gray-400"}`}>
                {opt.unit}
              </p>
              <h3 className={`font-bold text-base mb-4 ${opt.highlight ? "text-white" : "text-[#1A365D]"}`}>
                {opt.label}
              </h3>
              {opt.emergency && (
                <p className="text-xs text-gray-400 italic mb-3 leading-relaxed">
                  {emergencyDescription}
                </p>
              )}
              <ul className="space-y-2 flex-1">
                {opt.features.map((f, j) => {
                  const isShutdown = f === "Not affected by government shutdowns";
                  const isAgent = f === "Dedicated agent assigned to your case";
                  const isUpdates = f === "Daily status updates — never left guessing";
                  const isNoAgent = f === "No dedicated agent — you are on your own";
                  const isMailUpdates = f === "Updates only by physical mail letter";
                  const isNoContact = f === "No phone or email contact for your case";
                  const isAgentBlock = isAgent || isUpdates;
                  const isNoAgentBlock = isNoAgent || isMailUpdates || isNoContact;
                  const showTopDivider = isShutdown || isAgent || isNoAgent;

                  return (
                    <li
                      key={j}
                      className={`flex items-start gap-2 text-sm ${
                        showTopDivider
                          ? `mt-3 pt-3 border-t ${opt.highlight ? "border-white/20" : "border-gray-200"}`
                          : ""
                      }`}
                    >
                      <i
                        className={`flex-shrink-0 mt-0.5 ${
                          isShutdown
                            ? "ri-shield-check-line text-green-400"
                            : isAgentBlock
                            ? opt.highlight
                              ? "ri-user-line text-[#F6C344]"
                              : "ri-user-line text-[#1A365D]"
                            : isNoAgentBlock
                            ? "ri-close-line text-red-400"
                            : opt.muted
                            ? "ri-close-line text-red-400"
                            : opt.highlight
                            ? "ri-check-line text-[#F6C344]"
                            : "ri-check-line text-[#F6C344]"
                        }`}
                      ></i>
                      <span
                        className={`${
                          isShutdown
                            ? opt.highlight
                              ? "text-green-300 font-semibold"
                              : "text-green-700 font-semibold"
                            : isAgentBlock
                            ? opt.highlight
                              ? "text-white font-semibold"
                              : "text-[#1A365D] font-semibold"
                            : isNoAgentBlock
                            ? "text-gray-700 font-semibold"
                            : opt.highlight
                            ? "text-white/80"
                            : opt.muted
                            ? "text-gray-600"
                            : "text-gray-600"
                        }`}
                      >
                        {f}
                      </span>
                    </li>
                  );
                })}
              </ul>
              {opt.emergency && (
                <div className="mt-6">
                  <a
                    href="#comparison"
                    className="inline-flex items-center justify-center gap-2 border border-gray-300 hover:border-[#1A365D] text-gray-500 hover:text-[#1A365D] font-semibold text-sm px-4 py-2.5 rounded-md transition-all whitespace-nowrap w-full"
                  >
                    More Info <i className="ri-information-line"></i>
                  </a>
                </div>
              )}
              {opt.highlight && (
                <a
                  href="https://fastpassportcenter.com/checkout/"
                  className="mt-6 inline-flex items-center justify-center gap-2 bg-[#F6C344] hover:bg-[#e5b430] text-[#1A365D] font-bold text-sm px-4 py-3 rounded-md transition-all whitespace-nowrap"
                >
                  Start Now <i className="ri-arrow-right-line"></i>
                </a>
              )}
              {opt.smartSaver && (
                <a
                  href="https://fastpassportcenter.com/checkout/"
                  className="mt-6 inline-flex items-center justify-center gap-2 border border-[#1A365D]/20 hover:border-[#1A365D] text-[#1A365D] font-bold text-sm px-4 py-3 rounded-md transition-all whitespace-nowrap"
                >
                  Start Now <i className="ri-arrow-right-line"></i>
                </a>
              )}
              {opt.muted && (
                <a
                  href="#acceptance-facilities"
                  className="mt-6 inline-flex items-center justify-center gap-2 border border-gray-300 hover:border-gray-400 text-gray-400 hover:text-gray-600 font-semibold text-sm px-4 py-2.5 rounded-md transition-all whitespace-nowrap"
                >
                  More Info <i className="ri-information-line"></i>
                </a>
              )}
            </div>
          ))}
        </div>
        {afterCardsContent}
        <p className="text-center text-gray-400 text-xs mt-6">
          {disclaimerText}
        </p>
      </div>
    </section>
  );
};

export default SharedProcessingTimeSection;
export type { ProcessingOption };